import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from './ThemeContext';
import { 
  FaPhoneAlt, FaEnvelope, FaWhatsapp, FaInstagram, FaGithub, FaLinkedin,
  FaDownload, FaTimes, FaProjectDiagram, FaAddressCard,
  FaFilePdf, FaUserTie, FaMedium, FaDev, FaStar, FaCog, FaBlog
} from 'react-icons/fa';
import API_BASE_URL from '../config/api.js';
import '../css/Sidebar.css';

const verifyAccessCode = async (inputCode) => {
  if (!inputCode) return false;

  try {
    const response = await fetch(`${API_BASE_URL}/api/verify-access`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code: inputCode }),
    });
    return response.ok;
  } catch {
    return false;
  }
};

const Sidebar = ({ isOpen, toggleSidebar }) => {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();

  const handleNavigation = async (link, download = false, requireCode = false) => {
    if (requireCode) {
      const inputCode = prompt('Enter access code to view References:');
      const isValid = await verifyAccessCode(inputCode);
      if (!isValid) {
        alert('Incorrect code. Access denied.');
        return;
      }
    }

    // Check if the link is an external URL
    if (link.startsWith('http://') || link.startsWith('https://')) {
      window.open(link, '_blank');
    } else if (download) {
      window.open(link, '_blank');
    } else {
      navigate(link);
      toggleSidebar();
    }
  };

  const handleServicesReviews = (type) => {
    if (type === 'services') {
      navigate('/services');
    } else if (type === 'reviews') {
      navigate('/reviews');
    }
    toggleSidebar();
  };

  const handleArticleBlogButtons = (type) => {
    if (type === 'medium') {
      window.open('https://medium.com/@shivashanker7337', '_blank');
    } else if (type === 'devto') {
      window.open('https://dev.to/shiva_shanker_dec82951917', '_blank');
    } else if (type === 'blog') {
      window.open('https://shivawhispers.blogspot.com/', '_blank');
    }
  };

  const handleReferenceResumeButtons = async (type) => {
    if (type === 'references') {
      const inputCode = prompt('Enter access code to view References:');
      const isValid = await verifyAccessCode(inputCode);
      if (!isValid) {
        alert('Incorrect code. Access denied.');
        return;
      }
      navigate('/references');
      toggleSidebar();
    } else if (type === 'resume') {
      window.open('/cv.pdf', '_blank'); 
    }
  };

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        toggleSidebar();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, toggleSidebar]);

  const menuItems = [
    { className: 'div1', link: '/projects', label: 'My Work', icon: <FaProjectDiagram /> },
    { className: 'div2', link: '/github-projects', label: 'GitHub Projects', icon: <FaGithub /> },
    { 
      className: 'div3', 
      label: '', 
      splitButtons: true 
    },
    { 
      className: 'div4', link: '/contact', label: 'Get in Touch', icon: <FaEnvelope />, icons: true 
    },
    { className: 'div5', link: '/about', label: 'About Me', icon: <FaAddressCard /> },
    { 
      className: 'div6', 
      label: 'Tech Articles', 
      articleBlogButtons: true 
    },
    { 
      className: 'div7', 
      label: 'Resume', 
      referenceResumeButtons: true 
    }
  ];

  return (
    <>
      <nav
        className={`side-menu-btn1 ${isDarkMode ? 'side-menu-btn1--dark' : 'side-menu-btn1--light'}`}
        aria-label="Quick navigation"
      >
        <button
          type="button"
          onClick={() => window.history.back()}
          className="nav-btn1"
          aria-label="Back"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24"
            fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path stroke="none" d="M0 0h24v24H0z" fill="none" />
            <path d="M19 12H5" />
            <path d="M12 19l-7 -7l7 -7" />
          </svg>
          <span className="nav-btn1-label">Back</span>
        </button>
        <button
          type="button"
          onClick={() => navigate('/')}
          className="nav-btn1"
          aria-label="Home"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24"
            fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path stroke="none" d="M0 0h24v24H0z" fill="none" />
            <path d="M5 12l-2 0l9 -9l9 9l-2 0" />
            <path d="M5 12v7a2 2 0 0 0 2 2h10a2 2 0 0 0 2 -2v-7" />
            <path d="M9 21v-6a2 2 0 0 1 2 -2h2a2 2 0 0 1 2 2v6" />
          </svg>
          <span className="nav-btn1-label">Home</span>
        </button>
        <button
          type="button"
          onClick={toggleSidebar}
          className="nav-btn1"
          aria-label="Menu"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24"
            fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path stroke="none" d="M0 0h24v24H0z" fill="none" />
            <path d="M4 6h16" />
            <path d="M4 12h16" />
            <path d="M4 18h16" />
          </svg>
          <span className="nav-btn1-label">Menu</span>
        </button>
      </nav>

      <div
        className={`portfolio-popup ${isOpen ? 'active' : ''} ${isDarkMode ? 'portfolio-popup--dark' : 'portfolio-popup--light'}`}
        role="dialog"
        aria-modal="true"
        aria-label="Navigation menu"
      >
        <button
          type="button"
          className="menu-backdrop"
          onClick={toggleSidebar}
          aria-label="Close menu"
          tabIndex={isOpen ? 0 : -1}
        />
        <div className="menu-popup-panel">
        <div className="dashboard-container">
          <div className="dashboard-header">
            <h2 className="menu-panel-title">Menu</h2>
            <button type="button" className="menu-close-btn" onClick={toggleSidebar} aria-label="Close menu">
              <FaTimes />
            </button>
          </div>

          <div className="dashboard-content">
            <div className="dashboard-main">
              <div className="parent">
                {menuItems.map((item, index) => (
                  <div
                    key={index}
                    className={`${item.className} clickable-container ${item.splitButtons ? 'split-container' : ''} ${item.articleBlogButtons ? 'article-blog-container' : ''} ${item.referenceResumeButtons ? 'reference-resume-container' : ''}`}
                    onClick={item.splitButtons || item.articleBlogButtons || item.referenceResumeButtons ? undefined : () => handleNavigation(item.link, item.download, item.requireCode)}
                  >
                    <div className="container-content">
                      {item.download ? (
                        <button className="download-button">
                          <div className="docs">
                            {item.icon} {item.label}
                          </div>
                          <div className="download">
                            <FaDownload />
                          </div>
                        </button>
                      ) : item.splitButtons ? (
                        <>
                          <div className="split-buttons">
                            <button 
                              className="split-btn services-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleServicesReviews('services');
                              }}
                            >
                              <FaCog />
                              <span>Services</span>
                            </button>
                            <button 
                              className="split-btn reviews-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleServicesReviews('reviews');
                              }}
                            >
                              <FaStar />
                              <span>Reviews</span>
                            </button>
                          </div>
                        </>
                      ) : item.articleBlogButtons ? (
                        <>
                          <h2>{item.label}</h2>
                          <div className="article-blog-buttons">
                            <button 
                              className="article-blog-btn medium-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleArticleBlogButtons('medium');
                              }}
                            >
                              <FaMedium />
                              <span>Medium</span>
                            </button>
                            <button 
                              className="article-blog-btn devto-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleArticleBlogButtons('devto');
                              }}
                            >
                              <FaDev />
                              <span>Dev.to</span>
                            </button>
                           
                          </div>
                        </>
                      ) : item.referenceResumeButtons ? (
                        <>
                          <h2>{item.label}</h2>
                          <div className="reference-resume-buttons">
                          
                            <button 
                              className="reference-resume-btn resume-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleReferenceResumeButtons('resume');
                              }}
                            >
                              <FaFilePdf />
                              <span>Resume</span>
                            </button>
                          </div>
                        </>
                      ) : (
                        <>
                          <h2>{item.icon} {item.label}</h2>
                          {item.icons && (
                            <div className="icons-grid">
                              <a href="tel:+447867034729" className="icon-item" aria-label="Phone"><FaPhoneAlt /></a>
                              <a href="https://wa.me/+447867034729" className="icon-item" target="_blank" rel="noopener noreferrer" aria-label="WhatsApp"><FaWhatsapp /></a>
                              <a href="mailto:shivashanker7337@gmail.com" className="icon-item" aria-label="Email"><FaEnvelope /></a>
                              <a href="https://github.com/shivas1432" className="icon-item" target="_blank" rel="noopener noreferrer" aria-label="GitHub"><FaGithub /></a>
                              <a href="https://www.linkedin.com/in/shiva-kanugula-51a512252" className="icon-item" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn"><FaLinkedin /></a>
                              <a href="https://www.instagram.com/ss_web_innovations" className="icon-item" target="_blank" rel="noopener noreferrer" aria-label="Instagram"><FaInstagram /></a>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;